import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { DealerSearch } from '@features/dealers/components/DealerSearch/DealerSearch';
import { useQuote } from '../../context/QuoteContext';
import { useRevokedDealerSelection } from '../../hooks/useRevokedDealerSelection';
import { useClearDealerWhenNoneSell } from '../../hooks/useClearDealerWhenNoneSell';
import { NoSellingDealerError } from '../NoSellingDealerError';
import type { Dealer } from '@features/dealers/types/dealers.types';
import { PageHeader } from '@/shared/components';

const REVOKED_SELECTION_MESSAGE =
    'The dealer previously selected for this quote is no longer available to you. Please choose another dealer.';

interface DealerSelectionStepProps {
    /**
     * Advance-to-next-step callback supplied by QuoteWizard, bound to the
     * wizard's advance action (see useQuoteAdvance). Optional
     * so the step still renders in isolation without auto-advancing.
     */
    onAdvance?: () => void;
}

export const DealerSelectionStep: React.FC<DealerSelectionStepProps> = ({ onAdvance }) => {
    const { selectedDealer, selectDealer, selectedProduct } = useQuote();
    // Marks a fresh user selection so the post-commit effect auto-advances only
    // on a click, not when returning to this step with a dealer already chosen.
    const advanceRequestedRef = useRef(false);

    const handleDealerSelect = (dealer: Dealer | null) => {
        // DealerSearch's onSelectDealer contract is `Dealer | null` (its
        // handleClearSelection emits null). Only request advance on a positive
        // selection. The null branch is defensive: the wizard suppresses
        // DealerSearch's visible Clear affordance (a selectedDealer prop is always
        // passed), so a null only arrives via the standalone embedding, but the
        // guard keeps a cleared selection from advancing wherever the step is used.
        if (dealer) {
            advanceRequestedRef.current = true;
        }
        selectDealer(dealer);
    };

    // Advance only after selectDealer is reflected in quote state. Advancing
    // synchronously inside handleDealerSelect reads a stale canProceed (the
    // dispatch had not yet committed), so the first click would never move the
    // wizard. Running it from this effect guarantees the shared advance guard
    // (canProceed) and nextStep see the freshly selected dealer.
    useEffect(() => {
        if (advanceRequestedRef.current && selectedDealer) {
            advanceRequestedRef.current = false;
            onAdvance?.();
        }
    }, [selectedDealer, onAdvance]);

    const { retry: retryRetainedDealerCheck } = useRevokedDealerSelection(
        selectedDealer?.id,
        () => {
            selectDealer(null);
            toast.info(REVOKED_SELECTION_MESSAGE);
        },
        selectedProduct?.id,
    );

    const reportUnsearchedEmpty = useClearDealerWhenNoneSell(
        !!selectedProduct,
        !!selectedDealer,
        () => selectDealer(null),
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between content-start items-start">
                <PageHeader
                    title="Select Dealer"
                    subtitle="Please select from your available dealers below."
                />
            </div>
            <DealerSearch
                onSelectDealer={handleDealerSelect}
                selectedDealer={selectedDealer}
                sellingProductId={selectedProduct?.id}
                onRetry={retryRetainedDealerCheck}
                onUnsearchedEmptyChange={reportUnsearchedEmpty}
                emptyState={selectedProduct ? <NoSellingDealerError /> : undefined}
            />
        </div>
    );
};
